import { useState, type FormEvent } from "react";
import { useListTasks } from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Plus, CheckCircle2, Circle, Clock, XCircle, ArrowUp, ArrowRight, ArrowDown, AlertTriangle } from "lucide-react";
import InfoTip from "@/components/info-tip";

const statusIcons: Record<string, typeof Circle> = {
  open: Circle,
  in_progress: Clock,
  completed: CheckCircle2,
  cancelled: XCircle,
};

const priorityIcons: Record<string, typeof ArrowUp> = {
  urgent: AlertTriangle,
  high: ArrowUp,
  medium: ArrowRight,
  low: ArrowDown,
};

const priorityStyles: Record<string, string> = {
  urgent: "text-red-600",
  high: "text-orange-500",
  medium: "text-yellow-600",
  low: "text-gray-400",
};

const statusStyles: Record<string, string> = {
  open: "bg-blue-100 text-blue-800",
  in_progress: "bg-amber-100 text-amber-800",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-gray-100 text-gray-600",
};

export default function TasksPage({ cohortSlug }: { cohortSlug?: string } = {}) {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const { toast } = useToast();

  const { data: tasks, isLoading, refetch } = useListTasks({
    status: statusFilter !== "all" ? statusFilter as any : undefined,
    type: typeFilter !== "all" ? typeFilter as any : undefined,
  });

  const handleCreate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyId: parseInt(form.get("companyId") as string),
          title: form.get("title"),
          description: form.get("description") || null,
          type: form.get("type") || "task",
          priority: form.get("priority") || "medium",
          assignedTo: form.get("assignedTo") || null,
          createdBy: "operator",
        }),
      });
      if (!res.ok) throw new Error("Failed to create");
      toast({ title: "Created", description: "Task created successfully" });
      setDialogOpen(false);
      refetch();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handleStatusChange = async (taskId: number, newStatus: string) => {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/tasks/${taskId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error("Failed to update");
      refetch();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-8" data-testid="page-tasks">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-serif font-bold tracking-tight">Tasks &amp; Asks</h1>
            <InfoTip content="Tasks are things the ops team needs to do (e.g., 'Review NovaPay pitch deck'). Asks are requests from founders to the ops team (e.g., 'Need intro to Series A investors'). This page tracks both across every company in the portfolio." />
          </div>
          <p className="text-sm text-muted-foreground mt-1">Track operator tasks and founder asks across the portfolio</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="task">Tasks</SelectItem>
              <SelectItem value="ask">Asks</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm"><Plus className="w-4 h-4 mr-1" /> New</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create Task / Ask</DialogTitle></DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div><Label htmlFor="title">Title</Label><Input id="title" name="title" required /></div>
                <div><Label htmlFor="description">Description</Label><Textarea id="description" name="description" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label htmlFor="companyId">Company ID</Label><Input id="companyId" name="companyId" type="number" required /></div>
                  <div>
                    <Label htmlFor="type">Type</Label>
                    <select name="type" id="type" className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm">
                      <option value="task">Task</option>
                      <option value="ask">Ask</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="priority">Priority</Label>
                    <select name="priority" id="priority" className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm">
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div><Label htmlFor="assignedTo">Assigned To</Label><Input id="assignedTo" name="assignedTo" /></div>
                </div>
                <Button type="submit" className="w-full">Create</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : !tasks?.length ? (
        <Card><CardContent className="p-8 text-center text-muted-foreground">No tasks found</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {tasks.map((task: any) => {
            const StatusIcon = statusIcons[task.status] || Circle;
            const PriorityIcon = priorityIcons[task.priority] || ArrowRight;
            return (
              <Card key={task.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => handleStatusChange(task.id, task.status === "completed" ? "open" : "completed")}
                        className="mt-0.5"
                      >
                        <StatusIcon className={`w-4 h-4 ${task.status === "completed" ? "text-emerald-600" : "text-muted-foreground"}`} />
                      </button>
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`font-medium text-sm ${task.status === "completed" ? "line-through text-muted-foreground" : ""}`}>
                            {task.title}
                          </span>
                          <Badge variant="outline" className={`text-[10px] ${task.type === "ask" ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700"}`}>
                            {task.type}
                          </Badge>
                          <PriorityIcon className={`w-3 h-3 ${priorityStyles[task.priority]}`} />
                        </div>
                        {task.description && <p className="text-xs text-muted-foreground">{task.description}</p>}
                        <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                          <span>{task.companyName}</span>
                          {task.assignedTo && <span>&middot; {task.assignedTo}</span>}
                          {task.dueDate && <span>&middot; Due {new Date(task.dueDate).toLocaleDateString()}</span>}
                        </div>
                      </div>
                    </div>
                    <Badge variant="outline" className={statusStyles[task.status]}>
                      {task.status.replace("_", " ")}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
